import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import MikhalAvatar from '../components/MikhalAvatar';
import { Target, Heart, Zap, Users, Building2, MapPin, Award } from 'lucide-react';

const VALORES = [
  { icon: Target,    cor:'from-blue-500 to-blue-700',    titulo:'Missão',     desc:'Conectar talentos e empresas no Acre com tecnologia acessível, democratizando o acesso ao mercado de trabalho em todos os 22 municípios do estado.' },
  { icon: Heart,     cor:'from-red-500 to-rose-600',     titulo:'Valores',    desc:'Transparência, inovação, impacto social. Acreditamos que todo acreano merece acesso a oportunidades de trabalho dignas e alinhadas com seu perfil.' },
  { icon: Zap,       cor:'from-yellow-500 to-amber-600', titulo:'Visão',      desc:'Ser a plataforma de referência em empregos no norte do Brasil, unindo tecnologia de IA com o desenvolvimento econômico e social da Amazônia.' },
];

const NUMEROS = [
  { n:'22', l:'Municípios atendidos', icon:'🗺️' },
  { n:'30s', l:'Para criar um currículo com IA', icon:'⚡' },
  { n:'5', l:'Dimensões de análise de match', icon:'🧠' },
  { n:'100%', l:'Gratuito para candidatos', icon:'💚' },
];

const EQUIPE = [
  { nome:'Mikhal IA', cargo:'Assistente de Inteligência Artificial', desc:'Nossa IA especializada em carreiras, criada para ajudar candidatos a descobrir seu perfil profissional e empresas a encontrar os melhores talentos do Acre.' },
];

export default function QuemSomos() {
  return (
    <div className="min-h-screen bg-[#f9fafb]">
      <Navbar/>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0a1f12] via-[#0f2318] to-[#1a3820] py-20 px-5 text-center relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-acre-green/10 rounded-full blur-3xl"/>
        <div className="max-w-3xl mx-auto relative z-10">
          <span className="inline-flex items-center gap-2 bg-acre-gold/15 text-acre-gold border border-acre-gold/25 text-xs font-bold px-4 py-1.5 rounded-full mb-5">
            🌿 Nossa História
          </span>
          <h1 className="font-sora text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
            Quem somos
          </h1>
          <p className="text-white/70 text-base max-w-xl mx-auto leading-relaxed">
            O <strong className="text-white">AcreJob</strong> nasceu da necessidade de transformar
            a forma como candidatos e empresas se conectam no estado do Acre, usando tecnologia
            de ponta para criar oportunidades reais em toda a Amazônia.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 py-14 space-y-16">

        {/* Nossa história */}
        <section>
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-sora text-2xl font-extrabold text-acre-dark mb-4">
                A plataforma que o Acre precisava
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                O Acre possui 22 municípios e milhares de profissionais em busca de oportunidades.
                Por muito tempo, a distância entre candidatos qualificados e empresas que precisavam
                de talentos era enorme — falta de informação, processos lentos e pouco acesso à tecnologia.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                O <strong>AcreJob</strong> surgiu para mudar isso. Com um algoritmo de match
                multidimensional baseado em pesquisas científicas internacionais, conectamos
                candidatos e empresas com precisão e velocidade, independente de onde estejam no Acre.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed">
                Nossa IA, a <strong>Mikhal</strong>, cria perfis profissionais completos em minutos,
                identificando Hard Skills, Soft Skills e Fit Cultural de cada candidato — tecnologia
                que antes era acessível apenas a grandes corporações.
              </p>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 -m-4 rounded-full border-2 border-acre-green/20 animate-pulse"/>
                <MikhalAvatar size={200} animated className="drop-shadow-xl"/>
              </div>
            </div>
          </div>
        </section>

        {/* Missão, Valores, Visão */}
        <section>
          <div className="text-center mb-8">
            <h2 className="font-sora text-2xl font-extrabold text-acre-dark">
              Propósito e Valores
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {VALORES.map(v => {
              const Icon = v.icon;
              return (
                <div key={v.titulo} className="card p-6">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${v.cor} flex items-center justify-center mb-4 shadow-md`}>
                    <Icon size={22} className="text-white"/>
                  </div>
                  <h3 className="font-sora font-bold text-acre-dark mb-2">{v.titulo}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Números */}
        <section className="bg-gradient-to-br from-acre-dark to-[#1a3820] rounded-3xl p-10 text-center">
          <h2 className="font-sora text-2xl font-extrabold text-white mb-8">
            AcreJob em números
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {NUMEROS.map(n => (
              <div key={n.l}>
                <p className="text-3xl mb-1">{n.icon}</p>
                <p className="font-sora font-black text-3xl text-acre-gold">{n.n}</p>
                <p className="text-white/50 text-xs mt-1">{n.l}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Nossa Tecnologia */}
        <section>
          <h2 className="font-sora text-2xl font-extrabold text-acre-dark mb-6 text-center">
            Tecnologia que faz a diferença
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { icon:'🧠', titulo:'Algoritmo de Match v2.0',    desc:'5 dimensões de compatibilidade: área, localização, escolaridade, experiência e hard skills. Baseado em pesquisas internacionais (Gale-Shapley, Cosine Similarity).' },
              { icon:'🤖', titulo:'Mikhal IA',                  desc:'Assistente de IA que cria perfis profissionais completos em minutos, identificando pontos fortes, soft skills e fit cultural do candidato.' },
              { icon:'💬', titulo:'Chat Integrado',             desc:'Sistema de mensagens diretas entre empresas e candidatos. Sem sair da plataforma, com histórico completo de conversas.' },
              { icon:'📊', titulo:'Analytics em Tempo Real',    desc:'Empresas visualizam o score de compatibilidade de cada candidato. Candidatos acompanham seu progresso e recebem alertas de novas vagas.' },
            ].map(t => (
              <div key={t.titulo} className="card p-5 flex items-start gap-4">
                <span className="text-3xl flex-shrink-0">{t.icon}</span>
                <div>
                  <p className="font-sora font-bold text-acre-dark text-sm mb-1">{t.titulo}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-8">
          <h2 className="font-sora text-2xl font-extrabold text-acre-dark mb-3">
            Faça parte da transformação do Acre
          </h2>
          <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
            Seja você um candidato em busca do emprego ideal ou uma empresa buscando talentos,
            o AcreJob tem a tecnologia certa para te ajudar.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/cadastro?tipo=candidato" className="btn-primary flex items-center gap-2 px-6 py-3">
              <Users size={15}/> Sou Candidato — Grátis
            </Link>
            <Link to="/cadastro?tipo=empresa" className="btn-gold flex items-center gap-2 px-6 py-3">
              <Building2 size={15}/> Sou Empresa — 15 dias grátis
            </Link>
          </div>
          <p className="mt-5 text-xs text-gray-400 flex items-center justify-center gap-1.5">
            <MapPin size={12}/>Rio Branco, Acre, Brasil · contato@acrejob.com.br
          </p>
        </section>
      </div>
    </div>
  );
}
